import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, GlobalStyles } from '@mui/material';
import { AuthProvider, useAuth, UserRole } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import LoginForm from './components/auth/LoginForm';
import DashboardLayout from './components/layout/DashboardLayout';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import Analytics from './pages/Analytics';
import CompletedSamplesAdmin from './pages/CompletedSamplesAdmin';
import SamplesList from './pages/samples/SamplesList';
import SampleDetail from './pages/samples/SampleDetail';
import QRScanner from './pages/QRScanner';
import Clients from './pages/Clients';
import ClientDashboard from './pages/ClientDashboard';
import Reception from './pages/Reception';
import TVDashboard from './pages/TVDashboard';

import Analysis from './pages/Analysis';
import Validation from './pages/Validation';
import CertificatesNew from './pages/CertificatesNew';
import ResultsEntry from './pages/ResultsEntry';
import ResultsValidation from './pages/ResultsValidation';
import UserManagement from './pages/UserManagement';
import MethodManagementPage from './pages/MethodManagement';
import RechercheRapide from './pages/RechercheRapide';
import AuditTrailPage from './pages/AuditTrailPage';
import QuickActionsPage from './pages/QuickActionsPage';
import TransferDashboard from './pages/TransferDashboard';
import LocationManagement from './pages/LocationManagement';
import MultiSiteDashboard from './pages/MultiSiteDashboard';
import ScalesManagement from './pages/admin/ScalesManagement';
import SyncConfiguration from './pages/admin/SyncConfiguration';
import ReferenceMaterialsCatalogPage from './pages/admin/ReferenceMaterialsCatalog';
import ReclamationsManagement from './pages/admin/ReclamationsManagement';
import QcControlCharts from './pages/admin/QcControlCharts';
// import AnalyticsDashboard from './components/analytics/AnalyticsDashboard';
import LaboratoryDashboardPage from './pages/LaboratoryDashboardPage';

// Role-specific dashboards
import ReceptionistDashboard from './components/dashboards/ReceptionistDashboard';
import TechnicianDashboard from './components/dashboards/TechnicianDashboard';
import AnalystDashboard from './components/dashboards/AnalystDashboard';
import QualityDashboard from './components/dashboards/QualityDashboard';

// Laboratory-specific components
import LaboratoryRouter from './components/laboratory/LaboratoryRouter';

// Laboratory-specific dashboards
import SAAPreparationChimiqueDashboard from './components/dashboards/SAAPreparationChimiqueDashboard';
import SAAAnalystDashboard from './components/dashboards/SAAAnalystDashboard';
import PhysicoChimiqueAnalystDashboard from './components/dashboards/PhysicoChimiqueAnalystDashboard';
import PhysicoChimiqueSupervisorDashboard from './components/dashboards/PhysicoChimiqueSupervisorDashboard';

// Create Modern AFRILAB Theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563eb', // Modern blue
      dark: '#1d4ed8',
      light: '#3b82f6',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#7c3aed', // Modern purple
      dark: '#6d28d9',
      light: '#8b5cf6',
      contrastText: '#ffffff',
    },
    error: {
      main: '#ef4444',
      dark: '#dc2626',
      light: '#f87171',
    },
    warning: {
      main: '#f59e0b',
      dark: '#d97706',
      light: '#fbbf24',
    },
    info: {
      main: '#06b6d4',
      dark: '#0891b2',
      light: '#22d3ee',
    },
    success: {
      main: '#10b981',
      dark: '#059669',
      light: '#34d399',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    divider: 'rgba(148, 163, 184, 0.12)',
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.3,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: '-0.015em',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
      letterSpacing: '-0.015em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      letterSpacing: '-0.01em',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      letterSpacing: '-0.01em',
    },
    button: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: '#cbd5e1 #f1f5f9',
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f5f9',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#cbd5e1',
            borderRadius: 4,
            '&:hover': {
              backgroundColor: '#94a3b8',
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          borderRadius: 16,
          border: '1px solid rgba(148, 163, 184, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
          padding: '10px 24px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid rgba(148, 163, 184, 0.1)',
        },
        elevation1: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#3b82f6',
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
                borderColor: '#2563eb',
              },
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid',
          '&.MuiAlert-standardSuccess': {
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderColor: 'rgba(16, 185, 129, 0.2)',
          },
          '&.MuiAlert-standardError': {
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderColor: 'rgba(239, 68, 68, 0.2)',
          },
          '&.MuiAlert-standardWarning': {
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            borderColor: 'rgba(245, 158, 11, 0.2)',
          },
          '&.MuiAlert-standardInfo': {
            backgroundColor: 'rgba(6, 182, 212, 0.1)',
            borderColor: 'rgba(6, 182, 212, 0.2)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          border: 'none',
        },
      },
    },
  },
});

// Global Styles for modern effects
const globalStyles = (
  <GlobalStyles
    styles={{
      '*': {
        boxSizing: 'border-box',
      },
      html: {
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
      body: {
        margin: 0,
        padding: 0,
        fontFamily: theme.typography.fontFamily,
        backgroundColor: theme.palette.background.default,
      },
      // Custom scrollbar styles
      '::-webkit-scrollbar': {
        width: 8,
        height: 8,
      },
      '::-webkit-scrollbar-track': {
        background: '#f1f5f9',
        borderRadius: 4,
      },
      '::-webkit-scrollbar-thumb': {
        background: 'linear-gradient(135deg, #cbd5e1, #94a3b8)',
        borderRadius: 4,
        '&:hover': {
          background: 'linear-gradient(135deg, #94a3b8, #64748b)',
        },
      },
      // Selection styles
      '::selection': {
        backgroundColor: 'rgba(37, 99, 235, 0.2)',
        color: '#1e293b',
      },
      // Focus styles
      '*:focus-visible': {
        outline: '2px solid #3b82f6',
        outlineOffset: 2,
        borderRadius: 4,
      },
    }}
  />
);

// Role-based Dashboard Component
const RoleBasedDashboard: React.FC = () => {
  const { user } = useAuth();

  if (!user) return null;

  console.log('🔍 RoleBasedDashboard - User:', user);
  console.log('🔍 RoleBasedDashboard - User Role:', user.role);
  console.log('🔍 RoleBasedDashboard - User Department:', user.department);
  console.log('🔍 RoleBasedDashboard - User Email:', user.email);

  // Helper function to detect if an analyst belongs to a specific laboratory
  const isLaboratoryAnalyst = (user: any): boolean => {
    // Check if the user's department indicates a specific laboratory
    const laboratoryDepartments = [
      'Laboratoire SAA',
      'Laboratoire ICP', 
      'Laboratoire AU',
      'Laboratoire Volumétrie',
      'Préparation Mécanique',
      'Service de Validation'
    ];
    
    return user.department && laboratoryDepartments.some(dept => 
      user.department.includes(dept)
    );
  };

  // Helper function to determine if user should use laboratory router based on email/role
  const shouldUseLaboratoryRouter = (user: any): boolean => {
    // Check if user is laboratory-specific based on email
    const laboratoryEmails = [
      'saa@afrilab.ma',
      'icp@afrilab.ma', 
      'au@afrilab.ma',
      'volumetrie@afrilab.ma',
      'sup.saa@afrilab.ma',
      'sup.icp@afrilab.ma',
      'sup.au@afrilab.ma',
      'sup.vol@afrilab.ma',
      'preparation@afrilab.ma',
      // New preparation chimique users
      'saa.prep@afrilab.ma',
      'icp.prep@afrilab.ma',
      'au.prep@afrilab.ma',
      'vol.prep@afrilab.ma'
    ];
    
    // Or check if they have specific laboratory-related roles
    const laboratoryRoles = [
      UserRole.SAA_PREP_CHIMIQUE,
      UserRole.SAA_ANALYST,
      UserRole.ICP_PREP_CHIMIQUE,
      UserRole.ICP_ANALYST,
      UserRole.AU_PREP_CHIMIQUE,
      UserRole.AU_ANALYST,
      UserRole.VOLUMETRIE_PREP_CHIMIQUE,
      UserRole.VOLUMETRIE_ANALYST,
      UserRole.PREPARATION_MECANIQUE,
      UserRole.TECHNICIAN_PREP,
      UserRole.VALIDATION_SERVICE
    ];

    return laboratoryEmails.includes(user.email) || 
           laboratoryRoles.includes(user.role) ||
           (user.role === UserRole.ANALYST && isLaboratoryAnalyst(user)) ||
           (user.role === UserRole.LABORATORY_MANAGER);
  };

  // Main routing logic based on user role and context
  switch (user.role) {
    case UserRole.ADMIN:
      return <AdminDashboard />;
      
    case UserRole.LABORATORY_MANAGER:
      return <LaboratoryRouter userId={user.id} />;
      
    case UserRole.SAMPLE_RECEIVER:
      return <ReceptionistDashboard />;
      
    case UserRole.QUALITY_MANAGER:
      // 🔥 FIX: Route to Physico-Chimique Supervisor Dashboard instead of generic Validation
      return <PhysicoChimiqueSupervisorDashboard />;
      
    // Preparation Chimique Specialists - Route to Laboratory Router
    case UserRole.SAA_PREP_CHIMIQUE:
    case UserRole.ICP_PREP_CHIMIQUE:
    case UserRole.AU_PREP_CHIMIQUE:
    case UserRole.VOLUMETRIE_PREP_CHIMIQUE:
      return <LaboratoryRouter userId={user.id} />;
      
    // Laboratory Analysts - Route to Laboratory Router  
    case UserRole.SAA_ANALYST:
    case UserRole.ICP_ANALYST:
    case UserRole.AU_ANALYST:
    case UserRole.VOLUMETRIE_ANALYST:
      return <LaboratoryRouter userId={user.id} />;
    
    // Physico-Chimique Analyst - Direct to Physico-Chimique Dashboard
    case UserRole.PHYSICO_CHIMIQUE_ANALYST:
      return <PhysicoChimiqueAnalystDashboard />;
      
    // Mechanical Preparation
    case UserRole.PREPARATION_MECANIQUE:
      return <LaboratoryRouter userId={user.id} />;
      
    // Validation Service
    case UserRole.VALIDATION_SERVICE:
      return <Validation />;

    // Generic Analyst - Check if should use laboratory router
    case UserRole.ANALYST:
      if (shouldUseLaboratoryRouter(user)) {
        return <LaboratoryRouter userId={user.id} />;
      }
      return <AnalystDashboard />;
      
    case UserRole.TECHNICIAN_PREP:
      // Check if this is a specialized preparation technician
      if (shouldUseLaboratoryRouter(user)) {
        return <LaboratoryRouter userId={user.id} />;
      }
      return <TechnicianDashboard />;
      
    case UserRole.CLIENT_VIEWER:
      return <ClientDashboard />;
      
    default:
      console.warn('🔥 Unknown user role, defaulting to workflow dashboard:', user.role);
      return <Dashboard />;
  }
};

// Role-based Route Guard
const RoleGuard: React.FC<{ allowedRoles: UserRole[]; children: React.ReactNode }> = ({ 
  allowedRoles, 
  children 
}) => {
  const { user } = useAuth();
  
  if (!user || !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

// App Routes Component
const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  return (
    <DashboardLayout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<RoleBasedDashboard />} />
        
        {/* TV Dashboard - Public display */}
        <Route path="/tv-dashboard" element={<TVDashboard />} />
        
        {/* Analytics Routes - Admin and Lab Manager only */}
        <Route 
          path="/analytics" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER]}>
              <Analytics />
            </RoleGuard>
          } 
        />
        
        {/* Completed Samples Admin - Admin and Lab Manager only */}
        <Route 
          path="/admin/completed-samples" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER]}>
              <CompletedSamplesAdmin />
            </RoleGuard>
          } 
        />
        
        {/* Sample Management Routes - Role-based access */}
        <Route 
          path="/samples" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.ANALYST, UserRole.TECHNICIAN_PREP, UserRole.SAMPLE_RECEIVER]}>
              <SamplesList />
            </RoleGuard>
          } 
        />
        <Route 
          path="/samples/:id" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.ANALYST, UserRole.TECHNICIAN_PREP, UserRole.SAMPLE_RECEIVER]}>
              <SampleDetail />
            </RoleGuard>
          } 
        />
        
        {/* Reception Routes - Sample Receiver only */}
        <Route 
          path="/reception" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.SAMPLE_RECEIVER]}>
              <Reception />
            </RoleGuard>
          } 
        />
        <Route 
          path="/samples/reception" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.SAMPLE_RECEIVER]}>
              <Reception />
            </RoleGuard>
          } 
        />
        
        {/* Analysis Routes - Analyst only */}
        <Route 
          path="/analysis" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.ANALYST]}>
              <Analysis />
            </RoleGuard>
          } 
        />
        <Route 
          path="/samples/analysis" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.ANALYST]}>
              <Analysis />
            </RoleGuard>
          } 
        />
        
        {/* Validation Routes - Quality Manager only */}
        <Route 
          path="/samples/validation" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.QUALITY_MANAGER]}>
              <Validation />
            </RoleGuard>
          } 
        />
        
        {/* Results Routes - Analyst and above */}
        <Route 
          path="/results/entry" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.ANALYST]}>
              <ResultsEntry />
            </RoleGuard>
          } 
        />
        <Route 
          path="/results/validation" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.QUALITY_MANAGER]}>
              <ResultsValidation />
            </RoleGuard>
          } 
        />
        <Route 
          path="/results/certificates" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.QUALITY_MANAGER]}>
              <CertificatesNew />
            </RoleGuard>
          } 
        />
        
        {/* Admin Routes - Admin and Lab Manager only */}
                <Route 
          path="/users" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN]}>
              <UserManagement />
            </RoleGuard>
          }
        />

        {/* Method Management - Admin only */}
        <Route 
          path="/methods" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN]}>
              <MethodManagementPage />
            </RoleGuard>
          }
        />

        {/* Reference Materials Catalog - Admin + Quality Manager */}
        <Route
          path="/admin/reference-materials"
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.QUALITY_MANAGER]}>
              <ReferenceMaterialsCatalogPage />
            </RoleGuard>
          }
        />

        {/* Certificate Reclamations - Admin + Lab Manager + Quality Manager */}
        <Route
          path="/admin/reclamations"
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.QUALITY_MANAGER]}>
              <ReclamationsManagement />
            </RoleGuard>
          }
        />

        {/* QC Control Charts - Admin + Lab Manager + Quality Manager */}
        <Route
          path="/admin/qc-charts"
          element={
            <RoleGuard
              allowedRoles={[
                UserRole.ADMIN,
                UserRole.LABORATORY_MANAGER,
                UserRole.QUALITY_MANAGER,
              ]}
            >
              <QcControlCharts />
            </RoleGuard>
          }
        />

        {/* Audit Trail - Admin only */}
        <Route 
          path="/admin/audit" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN]}>
              <AuditTrailPage />
            </RoleGuard>
          }
        />

        {/* Quick Actions - Admin only */}
        <Route 
          path="/admin/quick-actions" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN]}>
              <QuickActionsPage />
            </RoleGuard>
          }
        />

        {/* Recherche Rapide - Admin and Lab Manager */}
        <Route 
          path="/admin/recherche-rapide" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER]}>
              <RechercheRapide />
            </RoleGuard>
          }
        />
        
        {/** Analytics route temporarily disabled to avoid duplicate pages */}
        
        {/* Client Management Routes - Admin, Lab Manager, and Sample Receiver */}
        <Route 
          path="/clients" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.SAMPLE_RECEIVER]}>
              <Clients />
            </RoleGuard>
          } 
        />
        
        {/* Client Dashboard - Client viewers only */}
        <Route 
          path="/client-dashboard" 
          element={
            <RoleGuard allowedRoles={[UserRole.CLIENT_VIEWER]}>
              <ClientDashboard />
            </RoleGuard>
          } 
        />
        
        {/* QR Scanner - All lab roles */}
        <Route path="/qr-scanner" element={<QRScanner />} />
        
        {/* Laboratory Route - Uses LaboratoryRouter for automatic routing */}
        <Route 
          path="/laboratory" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.SAA_PREP_CHIMIQUE, UserRole.SAA_ANALYST, UserRole.ICP_PREP_CHIMIQUE, UserRole.ICP_ANALYST, UserRole.AU_PREP_CHIMIQUE, UserRole.AU_ANALYST, UserRole.VOLUMETRIE_PREP_CHIMIQUE, UserRole.VOLUMETRIE_ANALYST, UserRole.PHYSICO_CHIMIQUE_ANALYST, UserRole.PREPARATION_MECANIQUE, UserRole.VALIDATION_SERVICE, UserRole.ANALYST, UserRole.TECHNICIAN_PREP]}>
              <LaboratoryDashboardPage />
            </RoleGuard>
          } 
        />
        
        {/* Reports - Admin and Lab Manager only */}
        <Route 
          path="/reports/productivity" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER]}>
              <div>Rapports de productivité</div>
            </RoleGuard>
          } 
        />
        <Route 
          path="/reports/quality" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER]}>
              <div>Rapports de qualité</div>
            </RoleGuard>
          } 
        />
        <Route 
          path="/reports/delays" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER]}>
              <div>Rapports de retards</div>
            </RoleGuard>
          } 
        />
        
        {/* Transfer Dashboard - For sample transfers between locations */}
        <Route 
          path="/transfers" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.SAMPLE_RECEIVER, UserRole.PREPARATION_MECANIQUE]}>
              <TransferDashboard />
            </RoleGuard>
          } 
        />
        
        {/* Location Management - Admin only */}
        <Route 
          path="/admin/locations" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN]}>
              <LocationManagement />
            </RoleGuard>
          } 
        />
        
        {/* Scales Management - Admin only */}
        <Route 
          path="/admin/scales" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN]}>
              <ScalesManagement />
            </RoleGuard>
          } 
        />
        
        {/* Sync Configuration - Admin only */}
        <Route 
          path="/admin/sync" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN]}>
              <SyncConfiguration />
            </RoleGuard>
          } 
        />
        
        {/* Multi-Site Dashboard - Admin only */}
        <Route 
          path="/admin/multi-site" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER]}>
              <MultiSiteDashboard />
            </RoleGuard>
          } 
        />
        
        {/* Physico-Chimique Lab Routes */}
        <Route 
          path="/physico-chimique/analyse" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.PHYSICO_CHIMIQUE_ANALYST]}>
              <PhysicoChimiqueAnalystDashboard />
            </RoleGuard>
          } 
        />
        <Route 
          path="/physico-chimique/validation" 
          element={
            <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.QUALITY_MANAGER]}>
              <PhysicoChimiqueSupervisorDashboard />
            </RoleGuard>
          } 
        />
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </DashboardLayout>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {globalStyles}
        <ErrorBoundary>
          <AuthProvider>
            <ErrorBoundary>
              <Router>
                <ErrorBoundary>
                  <AppRoutes />
                </ErrorBoundary>
              </Router>
            </ErrorBoundary>
          </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App; 